"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelStateManager = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
/**
 * Manager responsible for model states.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `modelStateManager` property.
 */
class ModelStateManager {
    constructor(sessionEngineCore) {
        this._converter = viewer_shared_services_1.Converter.instance;
        this._httpClient = viewer_shared_services_1.HttpClient.instance;
        this._sessionEngineCore = sessionEngineCore;
    }
    get modelState() {
        return this._modelState;
    }
    set modelState(value) {
        this._modelState = value;
        this._modelStateId = value === null || value === void 0 ? void 0 : value.id;
    }
    get modelStateId() {
        return this._modelStateId;
    }
    set modelStateId(value) {
        this._modelStateId = value;
    }
    get modelStateValidationMode() {
        return this._modelStateValidationMode;
    }
    set modelStateValidationMode(value) {
        this._modelStateValidationMode = value;
    }
    /**
     * Creates a new model state with the given parameter values and optional image and data.
     *
     * @param parameterValues Key-value pairs of parameter IDs and their corresponding values
     * @param omitSessionParameterValues Whether to omit parameters that have not been explicitly set in the session
     * @param image Optional image to associate with the model state
     * @param data Optional additional data to associate with the model state
     * @param arScene Optional AR scene to associate with the model state
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the ID of the created model state
     */
    createModelState() {
        return __awaiter(this, arguments, void 0, function* (parameterValues = {}, omitSessionParameterValues = false, image, data, arScene, retry = false) {
            this._sessionEngineCore.utilsManager.checkAvailability();
            try {
                const promises = [];
                // process the parameters
                const parameterSet = {};
                promises.push(this._sessionEngineCore.fileUploadManager
                    .uploadFileParameters(parameterValues)
                    .then(() => {
                    // create a set of the current validated parameter values
                    for (const parameterId in this._sessionEngineCore
                        .parameterManager.parameters) {
                        // if the parameter has not been set, we do not include it in the parameter set if the omitSessionParameterValues flag is set
                        if (!(omitSessionParameterValues === true &&
                            parameterValues[parameterId] === undefined)) {
                            parameterSet[parameterId] = (" " +
                                this._sessionEngineCore.parameterManager.parameters[parameterId].stringify(parameterValues[parameterId])).slice(1);
                        }
                    }
                }));
                // process the image input
                let imageData;
                let imageArrayBuffer;
                if (image) {
                    promises.push(this._sessionEngineCore.utilsManager
                        .processImageInput(image)
                        .then((result) => {
                        imageData = result === null || result === void 0 ? void 0 : result.imageData;
                        imageArrayBuffer = result === null || result === void 0 ? void 0 : result.arrayBuffer;
                    }));
                }
                // process the arScene input
                let arSceneId;
                if (arScene) {
                    promises.push(this._converter
                        .convertToBlob(arScene)
                        .then((arSceneBlob) => new sdk_geometry_api_sdk_v2_1.GltfApi(this._sessionEngineCore.sdkConfig).uploadGltf(this._sessionEngineCore.sessionId, new File([arSceneBlob], "arScene.gltf", {
                        type: "model/gltf-binary",
                    }), sdk_geometry_api_sdk_v2_1.QueryGltfConversion.SCENE))
                        .then((arSceneResponseDto) => {
                        var _a;
                        arSceneId = (_a = arSceneResponseDto.gltf) === null || _a === void 0 ? void 0 : _a.sceneId;
                    }));
                }
                // wait for all promises to resolve
                yield Promise.all(promises);
                // create the model state
                const response = yield new sdk_geometry_api_sdk_v2_1.ModelStateApi(this._sessionEngineCore.sdkConfig).createModelState(this._sessionEngineCore.sessionId, {
                    parameters: parameterSet,
                    data: data,
                    image: imageData,
                    arSceneId: arSceneId,
                });
                if (imageData && imageArrayBuffer)
                    yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).uploadAsset(response.asset.modelState.href, imageArrayBuffer, response.asset.modelState.headers);
                return response.modelState.id;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.createModelState(parameterValues, omitSessionParameterValues, image, data, arScene, true);
            }
        });
    }
    /**
     * Customizes the session using the provided model state.
     *
     * @param modelState The model state ID or the model state response object
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the root tree node of the customized session
     */
    customizeWithModelState(modelState_1) {
        return __awaiter(this, arguments, void 0, function* (modelState, retry = false) {
            this._sessionEngineCore.utilsManager.checkAvailability();
            try {
                // get the model state if it is not already a response
                let response;
                if (typeof modelState === "string") {
                    response = yield new sdk_geometry_api_sdk_v2_1.ModelStateApi(this._sessionEngineCore.sdkConfig).getModelState(modelState);
                }
                else {
                    response = modelState;
                }
                if (!response.modelState)
                    return new viewer_shared_node_tree_1.TreeNode();
                // read out the parameter values from the model state
                for (const parameterId in response.modelState.parameters)
                    this._sessionEngineCore.parameterManager.parameters[parameterId].value = response.modelState.parameters[parameterId];
                return this._sessionEngineCore.customizationManager.customize();
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.customizeWithModelState(modelState, true);
            }
        });
    }
    /**
     * Retrieves a model state by its ID.
     *
     * @param modelStateId The ID of the model state to retrieve. If undefined, the current model state ID is used.
     * @returns Promise with the model state information
     */
    getModelState(modelStateId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability();
            try {
                const id = modelStateId || this._modelStateId;
                if (!id)
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.getModelState: No model state id available.");
                const response = yield new sdk_geometry_api_sdk_v2_1.ModelStateApi(this._sessionEngineCore.sdkConfig).getModelState(id);
                return response;
            }
            catch (e) {
                throw yield this._httpClient.convertError(e);
            }
        });
    }
}
exports.ModelStateManager = ModelStateManager;
//# sourceMappingURL=ModelStateManager.js.map